import React, { Fragment } from 'react';
import { Input, DatePicker, Form, InputNumber, Row, Col, Switch, Image, TimePicker, Divider, Alert } from 'antd';
import moment from 'moment';

import { antdRegRules } from '@untils/common';
import CombineSelect from '../combine-select';
import Uploader from '../uploader';
import UploaderApp from '../uploader-app';
import UploaderDoc from '../uploader-doc';
import UploaderFile from '../uploader-file';
import { AiDetectButton } from '../ai-detect-button';
import { requiredRules } from './config';

const { RangePicker } = DatePicker;
const { TextArea } = Input;
const { Item } = Form;

const switchForm = (item, functionMap) => {
  const {
    type,
    placeholder,
    request,
    prefix,
    antdProps = {},
    name,
    max,
    isVideo,
    openSearch,
    src = [],
    value,
    className,
    isShowUrl = false,
    fileBtnName = '上传文件',
    uploadEnvUrl = '',
    afterChangeFun: afterChangeFun,
    detectType,
  } = item;
  const props = {
    placeholder,
    allowClear: true,
    className: className,
  };

  switch (type) {
    case 'textarea':
      return <TextArea {...props} {...antdProps} />;
    case 'inputpassword':
      return <Input.Password {...props} {...antdProps} />;
    case 'inputnumber':
      // eslint-disable-next-line no-unused-vars
      const { allowClear, ...restProps } = props;

      return <InputNumber {...restProps} {...antdProps} min={antdProps.min ? antdProps.min : 0} />;
    case 'select':
      return (
        <CombineSelect
          request={functionMap[request]}
          {...props}
          antdProps={antdProps}
          api={request}
          type="select"
          openSearch={openSearch}
          onChange={e => {
            functionMap.controlChange(name, e);
            afterChangeFun && afterChangeFun(name, e);
          }}
        />
      );
    case 'cascader':
      return (
        <CombineSelect
          request={functionMap[request]}
          {...props}
          antdProps={antdProps}
          api={request}
          type="cascader"
          onChange={functionMap.controlChange.bind(this, name)}
        />
      );
    case 'radio':
      return (
        <CombineSelect
          request={functionMap[request]}
          {...props}
          antdProps={antdProps}
          api={request}
          type="radio"
          onChange={functionMap.controlChange.bind(this, name)}
        />
      );
    case 'checkbox':
      return (
        <CombineSelect
          request={functionMap[request]}
          {...props}
          antdProps={antdProps}
          api={request}
          type="checkbox"
          onChange={functionMap.controlChange.bind(this, name)}
        />
      );
    case 'rangepicker':
      antdProps.format = antdProps.format || 'YYYY-MM-DD HH:mm:ss';
      return <RangePicker {...props} {...antdProps} />;
    case 'timepicker':
      return <TimePicker.RangePicker {...props} {...antdProps} />;
    case 'switch':
      return <Switch {...antdProps} />;
    case 'datepicker':
      antdProps.format = antdProps.format || 'YYYY-MM-DD HH:mm:ss';
      return <DatePicker {...props} {...antdProps} showTime={{ defaultValue: moment('00:00:00', 'HH:mm:ss') }} />;
    case 'uploader':
      return (
        <Uploader
          {...antdProps}
          max={max}
          prefix={prefix}
          isVideo={isVideo}
          isShowUrl={isShowUrl}
          getUploadFiles={functionMap.fillUpload.bind(this, name)}
        />
      );
    case 'uploader-app':
      return (
        <UploaderApp
          getUploadFiles={functionMap.fillUpload.bind(this, name)}
          setOthersValue={functionMap.setOtherValue.bind(this)}
        />
      );
    case 'uploader-doc':
      return (
        <UploaderDoc
          finishUpload={functionMap.finishUpload}
          finishRemove={functionMap.finishRemove}
          envUrl={uploadEnvUrl}
          btnName={fileBtnName}
        />
      );
    case 'uploader-file':
      return <UploaderFile {...antdProps} />;
    case 'img':
      return src.filter(Boolean).map((item, index) => {
        // src 兼容字符串数组，以及带 AI 识别信息的对象数组
        const { url, image_ai_info: imageAiInfo } = typeof item === 'string' ? { url: item } : item;

        return (
          <div key={index} style={{ display: 'flex', alignItems: 'end' }}>
            <Image className="mr-12 mb-12" width={145} src={url} />
            {detectType && <AiDetectButton className="ml-12 mb-12" type={detectType} url={url} data={imageAiInfo} />}
          </div>
        );
      });
    case 'multi-img':
      return (
        <Image.PreviewGroup>
          {src.map((item, index) => (
            <Image className="mr-12 mb-12" width={145} src={item} key={index} />
          ))}
        </Image.PreviewGroup>
      );
    case 'video':
      return !src || src === '' ? (
        ''
      ) : (
        <video src={src} controls="controls" width={200}>
          浏览器不支持视频组件
        </video>
      );
    case 'span':
      return <p className={className}>{value}</p>;
    case 'hr':
      return (
        <Divider className={className} {...antdProps}>
          {value}
        </Divider>
      );
    case 'html':
      return <div className="form-html-wrap" dangerouslySetInnerHTML={{ __html: value }}></div>;
    case 'alert':
      return <Alert {...antdProps} />;
    case 'component':
      return value;
    default:
      return <Input {...props} {...antdProps} />;
  }
};

// 控制器控制显示隐藏
const domAbout = (controler, controlObj = {}, item) => {
  const { name, rules = [], label, isRequired, extra, type, width = '' } = item;
  const getValueFromEvent =
    type === 'uploader-file'
      ? e => (Array.isArray(e) ? e : e?.fileList).map(file => (file instanceof File ? file : file.originFileObj))
      : item.getValueFromEvent;
  let isDisplay = true;
  const hasRequire = rules.filter(item => item.required).length;

  isRequired && !hasRequire && rules.push(requiredRules(type, label));

  if (type === 'input' || !type) {
    rules.push(antdRegRules.frontBackSpace);
  }

  // 找到需要被控制的组件
  const realControler = controler.filter(item => {
    return item.target.indexOf(name) > -1 || item.target === name;
  });

  if (realControler.length > 0) {
    isDisplay = false;

    // 处理所有控制条件，任一条件满足即可
    const finalDisplay = realControler.some(controlItem => {
      const { controler, value, type = '' } = controlItem;
      let itemDisplay = false;

      // 多组数据控制，目前业务只需AND操作，OR操作后续遇到修改
      if (type) {
        if (type === 'and') {
          const filter = controler.filter((it, idx) => {
            const val = value[idx];

            return val.indexOf(controlObj[it]) < 0;
          });

          itemDisplay = filter.length === 0;
        }
      } else {
        itemDisplay = value.indexOf(controlObj[controler]) > -1;
      }

      return itemDisplay;
    });

    isDisplay = finalDisplay;
  }
  const itemProps = {
    name,
    rules,
    label,
    extra,
    width,
    getValueFromEvent,
  };

  return {
    isDisplay,
    itemProps,
  };
};

const uiType = (item, functionMap, controlObj, controler, width, transIdx) => {
  const { children = [] } = item;
  const { isDisplay, itemProps } = domAbout(controler, controlObj, item);

  const rowWidth = itemProps.width ? itemProps.width : width;

  return (
    <Fragment key={transIdx}>
      {isDisplay && (
        <Row key={transIdx} style={{ width: rowWidth }}>
          <Col span={24}>
            <div className="cell-form">
              <Item {...itemProps}>{switchForm(item, functionMap)}</Item>
              {children.length > 0 && (
                <div className="children-cell">
                  {children.map((item, index) => {
                    const { isDisplay, itemProps } = domAbout(controler, controlObj, item);

                    return (
                      <Fragment key={index}>
                        {isDisplay && (
                          <Item {...itemProps} key={index}>
                            {switchForm(item, functionMap)}
                          </Item>
                        )}
                      </Fragment>
                    );
                  })}
                </div>
              )}
            </div>
          </Col>
        </Row>
      )}
    </Fragment>
  );
};

export { uiType };
